import { useTranslation } from 'react-i18next';

import { Label, LABEL_TYPE } from '@/components/generic';
import { IMAGE, TEXT_OVERLAY } from '@/constants';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { UploadVideo } from '@/pages/Event/components/UploadVideo';
import { MusicIcon } from '@/resources';
import {
  ThemeProp,
  TributeVideoAsset,
  TributeVideoOptionsType,
  UpdateCreateTributeVideoPayload
} from '@/types';

type ReviewComponentProps = {
  photos: TributeVideoAsset[];
  music: TributeVideoAsset[];
  theme?: ThemeProp;
  tributeVideoData: UpdateCreateTributeVideoPayload;
  assets: TributeVideoAsset[];
  titleText: string | undefined;
  deceasedNameText: string | undefined;
  birthDeathText: string | undefined;
  relatedUsersCanGenerate?: boolean;
  hasMusicStep?: boolean;
  tributeVideoOptions: TributeVideoOptionsType;
};

export const ReviewComponent = ({
  photos,
  music,
  theme,
  tributeVideoData,
  assets,
  titleText,
  deceasedNameText,
  birthDeathText,
  relatedUsersCanGenerate,
  hasMusicStep,
  tributeVideoOptions
}: ReviewComponentProps) => {
  const [t] = useTranslation('common');

  return (
    <>
      {relatedUsersCanGenerate && (
        <>
          <Label
            text={t('videoOutput')}
            type={LABEL_TYPE.H3_BOLD}
            className='mb-2 text-primary-blue md:mb-0'
          />
          <UploadVideo
            tributeVideoData={tributeVideoData}
            assets={assets}
            titleText={titleText}
            deceasedNameText={deceasedNameText}
            birthDeathText={birthDeathText}
            tributeVideoOptions={tributeVideoOptions}
          />
          <div className='my-6 h-px w-full bg-secondary-blue-gray' />
        </>
      )}
      <div className='flex flex-col'>
        {music.length !== 0 && hasMusicStep && (
          <>
            <Label
              text={t('music')}
              type={LABEL_TYPE.H3_BOLD}
              className='md-12 mb-4 text-primary-blue'
            />
            {music.map((song, id) => (
              <div className='mt-3 flex flex-row items-center sm:mt-6 md:mt-2' key={id}>
                <MusicIcon fill='var(--color-primary-black)' className='mr-3 size-4' />
                <Label
                  text={song.asset.assetName.replaceAll('"', '') ?? ''}
                  type={LABEL_TYPE.PARAGRAPH2}
                  className='!font-bold'
                />
              </div>
            ))}
          </>
        )}

        {relatedUsersCanGenerate && theme && (
          <>
            <Label
              text={t('theme')}
              type={LABEL_TYPE.H3_BOLD}
              className='my-4 text-primary-blue md:mb-6'
            />
            <div className='mb-5 mt-2 size-[170px] md:size-[206px] 3xl:size-[283px] 4xl:size-[283px]'>
              <AssetItem
                setSelectedAsset={() => Promise.resolve()}
                setShowAsset={() => Promise.resolve()}
                deleteSelectedAsset={() => Promise.resolve()}
                asset={theme}
                canBeDeleted={false}
                selectedVideoUrl={''}
              />
            </div>
          </>
        )}

        {photos.length !== 0 && (
          <>
            {relatedUsersCanGenerate && (
              <div className='mb-6 mt-8 h-px w-full bg-secondary-blue-gray' />
            )}
            <Label
              text={t('photos')}
              type={LABEL_TYPE.H3_BOLD}
              className={`mb-6 text-primary-blue ${!relatedUsersCanGenerate && 'mt-6'}`}
            />
            <div className='grid grid-cols-2 items-center justify-between gap-4 md:grid-cols-5 md:gap-6'>
              {photos.length !== 0 &&
                photos?.map((asset: TributeVideoAsset, index: number) => (
                  <div key={index}>
                    <AssetItem
                      asset={asset.asset}
                      key={asset.asset.id}
                      canBeDeleted={false}
                      selectedVideoUrl={''}
                      showDurationMultiplier={
                        asset.asset.assetType === IMAGE || asset.asset.assetType === TEXT_OVERLAY
                      }
                      tributeVideoAsset={asset}
                    />
                  </div>
                ))}
            </div>
            <div className='mb-6 mt-4 h-px w-full bg-secondary-blue-gray' />
          </>
        )}
      </div>
    </>
  );
};
